// Single hook that owns both FPS surfaces the HUD reads:
//   - camera: rate of `requestVideoFrameCallback` firings on the video ref
//     (source frame rate off the video track — undisturbed by our sampler
//     throttle)
//   - processed: rate of completed `decodeActiveFormat` returns; the parent
//     calls `recordProcessed()` after each decode
//
// Timestamps live in refs so the rVFC loop stays cheap (no re-render per
// frame). The DOM-facing state updates on a 250 ms interval so the HUD
// re-renders 4×/s, not 30×/s.

import { useCallback, useEffect, useRef, useState } from 'react';
import { computeFps, pruneWindow } from '../lib/fpsWindow';

export interface FpsPair {
  camera: number | null;
  processed: number | null;
}

const DEFAULT_HUD_REFRESH_MS = 250;

export function useFpsPair(
  videoRef: React.MutableRefObject<HTMLVideoElement | null>,
  enabled: boolean = true
) {
  const cameraStampsRef = useRef<number[]>([]);
  const processedStampsRef = useRef<number[]>([]);
  const [display, setDisplay] = useState<FpsPair>({ camera: null, processed: null });

  // Camera-source FPS via requestVideoFrameCallback on the underlying
  // <video>. Effect re-runs when `enabled` flips true so we attach after
  // the camera stream is actually ready — otherwise `videoRef.current`
  // is null at mount and the rVFC never binds. rVFC is available in
  // Chromium, WebKit ≥15.4, and modern Firefox; older browsers silently
  // degrade to `null` for the camera value.
  useEffect(() => {
    if (!enabled) return;
    const v = videoRef.current;
    if (!v || typeof v.requestVideoFrameCallback !== 'function') return;
    let cancelled = false;
    let handleId = 0;
    const tick = (): void => {
      if (cancelled) return;
      const t = performance.now();
      cameraStampsRef.current.push(t);
      pruneWindow(cameraStampsRef.current, t);
      handleId = v.requestVideoFrameCallback(tick);
    };
    handleId = v.requestVideoFrameCallback(tick);
    return () => {
      cancelled = true;
      if (typeof v.cancelVideoFrameCallback === 'function') {
        v.cancelVideoFrameCallback(handleId);
      }
    };
  }, [videoRef, enabled]);

  // Display refresh: recompute from the rolling windows on a fixed
  // schedule so the DOM tick is decoupled from event volume. Skip the
  // setState when the rounded integers didn't change — the HUD renders
  // rounded values, so nothing on screen would change and we'd re-render
  // LivePreview's overlay 4×/s for no reason.
  useEffect(() => {
    const id = window.setInterval(() => {
      const t = performance.now();
      pruneWindow(cameraStampsRef.current, t);
      pruneWindow(processedStampsRef.current, t);
      const camera = computeFps(cameraStampsRef.current, t);
      const processed = computeFps(processedStampsRef.current, t);
      setDisplay((prev) => {
        if (roundedEqual(prev.camera, camera) && roundedEqual(prev.processed, processed)) {
          return prev;
        }
        return { camera, processed };
      });
    }, DEFAULT_HUD_REFRESH_MS);
    return () => window.clearInterval(id);
  }, []);

  const recordProcessed = useCallback(() => {
    processedStampsRef.current.push(performance.now());
  }, []);

  return { display, recordProcessed };
}

function roundedEqual(a: number | null, b: number | null): boolean {
  if (a === null && b === null) return true;
  if (a === null || b === null) return false;
  return Math.round(a) === Math.round(b);
}
